import { useState, useCallback } from 'react';
import { type CarouselSlide } from '@editor2/types/pageElements';
import { copyElement, getNewElement } from '@editor2/services/elementService';

export function useCarouselSlides(initSlides: CarouselSlide[]) {
	const [slides, setSlides] = useState<CarouselSlide[]>(initSlides);

	const handleAddSlide = useCallback((index?: number) => {
		const newSlide = getNewElement('Slide') as CarouselSlide;

		setSlides((prev) =>
			typeof index === 'number' && index >= 0 && index <= prev.length
				? [...prev.slice(0, index), newSlide, ...prev.slice(index)]
				: [...prev, newSlide]
		);
	}, []);

	const handleDuplicateSlide = useCallback((index: number) => {
		setSlides((prev) => [...prev.slice(0, index), copyElement(prev[index]), ...prev.slice(index)]);
	}, []);

	const handleDeleteSlide = useCallback((index: number) => {
		setSlides((prev) => [...prev.slice(0, index), ...prev.slice(index + 1)]);
	}, []);

	const handleSlideChange = useCallback((value: Partial<CarouselSlide>, index: number) => {
		setSlides((prev) => [
			...prev.slice(0, index),
			{ ...prev[index], ...value },
			...prev.slice(index + 1)
		]);
	}, []);

	const setAllSlides = useCallback((newSlides: CarouselSlide[]) => {
		setSlides(newSlides);
	}, []);

	return {
		slides,
		setAllSlides,
		handleAddSlide,
		handleDuplicateSlide,
		handleDeleteSlide,
		handleSlideChange
	};
}
